import { useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { getBrowser, getDeviceType } from '@/lib/botDetection';
import { getDeviceInfo } from '@/lib/trafficSourceDetection';
import { isProductionDomain } from '@/lib/productionDomainCheck';

function getVisitorId(): string {
  if (typeof window === 'undefined') return 'server';
  let id = localStorage.getItem('xpand_visitor_id');
  if (!id) {
    id = `v_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('xpand_visitor_id', id);
  }
  return id;
}

function getSessionId(): string {
  if (typeof window === 'undefined') return 'server';
  let id = sessionStorage.getItem('xpand_session_id');
  if (!id) {
    id = `s_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('xpand_session_id', id);
  }
  return id;
}

interface PerformanceData {
  loadTime: number | null;
  ttfb: number | null;
  fcp: number | null;
  lcp: number | null;
  cls: number | null;
  fid: number | null;
  domInteractive: number | null;
  domComplete: number | null;
}

export function usePerformanceTracking() {
  const hasTracked = useRef(false);
  const lcpValue = useRef<number | null>(null);
  const clsValue = useRef<number>(0);
  const fidValue = useRef<number | null>(null);
  const isProduction = useRef<boolean>(isProductionDomain());

  const trackPerformance = useCallback(async (data: PerformanceData) => {
    // Skip tracking for non-production environments
    if (!isProduction.current) return;
    try {
      const visitorId = getVisitorId();
      const sessionId = getSessionId();
      const deviceInfo = getDeviceInfo();

      await supabase.from('performance_metrics').insert({
        visitor_id: visitorId,
        session_id: sessionId,
        page_path: window.location.pathname,
        load_time_ms: data.loadTime,
        ttfb_ms: data.ttfb,
        fcp_ms: data.fcp,
        lcp_ms: data.lcp,
        cls: data.cls,
        fid_ms: data.fid,
        dom_interactive_ms: data.domInteractive,
        dom_complete_ms: data.domComplete,
        browser: getBrowser(),
        device_type: getDeviceType(),
        connection_type: deviceInfo.connectionType,
      });

      console.log('[Performance] Metrics tracked:', data);
    } catch (error) {
      console.error('[Performance] Error tracking metrics:', error);
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || hasTracked.current) return;

    // Observe LCP
    const lcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1];
      if (lastEntry) {
        lcpValue.current = Math.round(lastEntry.startTime);
      }
    });

    // Observe CLS
    const clsObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (!(entry as PerformanceEntry & { hadRecentInput?: boolean }).hadRecentInput) {
          clsValue.current += (entry as PerformanceEntry & { value?: number }).value || 0;
        }
      }
    });

    // Observe FID
    const fidObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      if (entries.length > 0) {
        fidValue.current = Math.round((entries[0] as PerformanceEntry & { processingStart?: number }).processingStart || 0 - entries[0].startTime);
      }
    });

    try {
      lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
      clsObserver.observe({ type: 'layout-shift', buffered: true });
      fidObserver.observe({ type: 'first-input', buffered: true });
    } catch (e) {
      // Some browsers don't support all metrics
      console.warn('[Performance] Some metrics not supported:', e);
    }

    // Track on page load complete
    const trackOnLoad = () => {
      if (hasTracked.current) return;
      hasTracked.current = true;

      // Wait a bit for all metrics to be collected
      setTimeout(() => {
        const timing = performance.timing;
        const navigationStart = timing.navigationStart;

        // Get FCP
        let fcp: number | null = null;
        const paintEntries = performance.getEntriesByType('paint');
        const fcpEntry = paintEntries.find(entry => entry.name === 'first-contentful-paint');
        if (fcpEntry) {
          fcp = Math.round(fcpEntry.startTime);
        }

        trackPerformance({
          loadTime: timing.loadEventEnd > 0 ? timing.loadEventEnd - navigationStart : null,
          ttfb: timing.responseStart > 0 ? timing.responseStart - navigationStart : null,
          fcp,
          lcp: lcpValue.current,
          cls: Math.round(clsValue.current * 1000) / 1000, // Round to 3 decimals
          fid: fidValue.current,
          domInteractive: timing.domInteractive > 0 ? timing.domInteractive - navigationStart : null,
          domComplete: timing.domComplete > 0 ? timing.domComplete - navigationStart : null,
        });
      }, 1000);
    };

    if (document.readyState === 'complete') {
      trackOnLoad();
    } else {
      window.addEventListener('load', trackOnLoad);
    }

    return () => {
      window.removeEventListener('load', trackOnLoad);
      try {
        lcpObserver.disconnect();
        clsObserver.disconnect();
        fidObserver.disconnect();
      } catch (e) {
        // Ignore errors on cleanup
      }
    };
  }, [trackPerformance]);

  return { trackPerformance };
}
